import { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, MessageCircleQuestion, TrendingDown } from "lucide-react";
import type { FAQ } from "@/hooks/useFAQs";

interface FAQSectionProps {
  faqs: FAQ[];
  treatmentName?: string;
  related_trends?: string[];
}

export const FAQSection = memo(function FAQSection({ faqs, treatmentName, related_trends }: FAQSectionProps) {
  if (!faqs || faqs.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10 flex-shrink-0">
            <MessageCircleQuestion className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <CardTitle as="h2" className="text-base sm:text-xl">Frequently Asked Questions</CardTitle>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 truncate">
              {treatmentName ? `Questions about ${treatmentName}` : 'Common questions from patients'}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pb-4 sm:pb-6">
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={faq.id} value={faq.id} className="border-b border-border/50">
              <AccordionTrigger className="text-left hover:no-underline py-3 sm:py-4 gap-2 sm:gap-3">
                <div className="flex items-start gap-2 sm:gap-3">
                  <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent mt-0.5 shrink-0" />
                  <span className="font-medium text-foreground text-sm sm:text-base">{faq.question}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pl-5 sm:pl-7 pb-3 sm:pb-4 text-xs sm:text-sm">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {related_trends && related_trends.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border/50">
            <h3 className="font-medium text-sm text-secondary mb-3 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-accent" />
              Popular Searches
            </h3>
            <div className="flex flex-wrap gap-2">
              {related_trends.map((keyword, i) => (
                <span 
                  key={i} 
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/5 text-secondary border border-secondary/10 hover:bg-secondary/10 transition-colors cursor-default"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
